import {
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BaseDto } from '@authority-labs/api-interfaces';
import { CrudDialog } from '@check-list/app/classes/crud-dialog';
import { GetManyDefaultResponse } from '@nestjsx/crud';
import {
  QueryFilter,
  QueryJoin,
  RequestQueryBuilder
} from '@nestjsx/crud-request';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SearchRequest } from '../../../classes/models/search-request-dto.model';
import { CrudService } from '../../services/crud/crud.service';

@Component({
  selector: 'authority-labs-entity-selector',
  templateUrl: './entity-selector.component.html',
  styleUrls: ['./entity-selector.component.scss']
})
export class EntitySelectorComponent<
  T extends BaseDto,
  S extends CrudService<T>
> implements OnInit, OnDestroy
{
  @Input() title: string;
  @Input() readonly: boolean;
  @Input() optionHeight = 32;
  @Input() optionsSize = 8;
  @Input() crudService: S;
  @Input() searchFields: string[];
  @Input() selectFields: string[];
  @Input() joinFields: QueryJoin[];
  @Input() optionTpl: TemplateRef<unknown>;
  @Input() selectedTpl: TemplateRef<unknown>;
  @Input() filters?: QueryFilter[];
  @Input() entityDialogService: CrudDialog<T>;
  @Input() canAdd = false;
  @Input() canEdit = false;

  entity: T;
  origEntityId: number | string;
  entities: T[] = [];
  isLoading = false;
  lastSearchValue: string | number;
  searchParams: SearchRequest = {
    value: '',
    searchFields: ['name'],
    selectFields: [],
    joinFields: [],
    page: 1,
    limit: 15,
    filters: []
  };

  private componentDestroyed$: Subject<boolean> = new Subject();

  @Input()
  getLabel(entity: T): string {
    return String(entity.id);
  }

  get canSelect(): boolean {
    return !!this.entity && this.origEntityId !== this.entity.id;
  }

  constructor(
    public dialogRef: MatDialogRef<T>,
    @Inject(MAT_DIALOG_DATA)
    data: { entityId: number | string; readonly: boolean }
  ) {
    this.origEntityId = data.entityId;
    this.readonly = data.readonly;
    // this.search = debounce(this.search, 500);
  }

  ngOnInit(): void {
    this.searchParams.searchFields =
      this.searchFields || this.searchParams.searchFields;
    this.searchParams.selectFields =
      this.selectFields || this.searchParams.selectFields;
    this.searchParams.joinFields =
      this.joinFields || this.searchParams.joinFields;
    this.searchParams.filters = this.filters || this.searchParams.filters;

    if (this.origEntityId) {
      const qb = RequestQueryBuilder.create();
      qb.setJoin(this.searchParams.joinFields);
      qb.setFilter(this.searchParams.filters);

      this.crudService
        .getOne(Number(this.origEntityId), qb.query())
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe((resp) => {
          this.afterInit([resp]);
        });
    }
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  afterInit(data: T[]): void {
    this.entity = data[0];
    this.entities = data;
  }

  search(value: string | number): Observable<GetManyDefaultResponse<T>> {
    this.isLoading = true;
    this.searchParams.value = value;
    this.searchParams.page =
      this.searchParams.value === this.lastSearchValue
        ? this.searchParams.page
        : 1;

    const sub = this.crudService.search(this.searchParams);
    sub.subscribe((resp) => {
      if (this.searchParams.value === this.lastSearchValue) {
        this.entities = [...this.entities, ...resp.data];
      } else {
        this.lastSearchValue = this.searchParams.value;
        this.entities = resp.data;
      }

      this.loadFinish();
    });

    return sub;
  }

  loadMore(): void {
    this.searchParams.page++;
    this.search(this.searchParams.value);
  }

  selectEntity(): void {
    this.dialogRef.close(this.entity);
  }

  onSelect(option: T): void {
    this.entity = option;
  }

  loadFinish(): void {
    this.isLoading = false;
  }

  newEntity() {
    this.entityDialogService
      .create()
      .afterClosed()
      .subscribe((entity) => {
        if (entity) {
          this.entities.push(entity);
          this.entity = entity;
        }
      });
  }

  editEntity() {
    this.entityDialogService
      .edit(this.entity)
      .afterClosed()
      .subscribe((entity: T) => {
        if (entity) {
          this.origEntityId = null;
          this.entity = entity;
        }
      });
  }
}
